/**
 * Dragging songs from one list onto another: Radio's library onto its log or
 * the coming-up boxes, and later between windows.
 *
 * The songs travel as their queue form (JSON) under their own type, so a drop
 * target needs no lookup and can tell a song drag from a row being reordered
 * (core/dragList.ts) or files dragged in from the desktop. While the pointer
 * moves only the type can be read (browsers hide drag data until the drop), so
 * targets decide with `carriesTracks` and read with `droppedTracks`.
 */

import type { DragEvent } from "react";
import type { QueueTrack } from "./player";

const TYPE = "application/x-nova-tracks";

export function startTrackDrag(e: DragEvent, tracks: QueueTrack[], reason?: string) {
  e.dataTransfer.effectAllowed = "copy";
  e.dataTransfer.setData(TYPE, JSON.stringify({ tracks, reason }));
  // Some webviews refuse a drag that carries no plain text.
  e.dataTransfer.setData("text/plain", tracks.map((t) => `${t.title} — ${t.artist}`).join("\n"));
}

export function carriesTracks(e: DragEvent): boolean {
  return Array.from(e.dataTransfer.types).includes(TYPE);
}

/** The songs dropped, and the reason they carry when they are clips (`clip:<type>`). */
export function droppedTracks(e: DragEvent): { tracks: QueueTrack[]; reason?: string } {
  try {
    const data = JSON.parse(e.dataTransfer.getData(TYPE)) as { tracks?: unknown; reason?: unknown };
    const tracks = Array.isArray(data.tracks) ? data.tracks.filter((t): t is QueueTrack => !!t && typeof (t as QueueTrack).id === "string") : [];
    return { tracks, reason: typeof data.reason === "string" ? data.reason : undefined };
  } catch {
    return { tracks: [] };
  }
}
